/**
 * Logiciels connectés : les logiciels que les agents IA consultent pendant
 * l'appel (envois, commandes, dossiers). Une connexion appartient au
 * compte et sert les agents cochés, comme les tables de référence.
 *
 * Toutes les connexions se créent et se modifient avec le même formulaire
 * (CustomApiDialog). Un modèle tout prêt ne fait que le préremplir ; une
 * intégration hors catalogue s'ouvre par le lien que donne le logiciel
 * partenaire : ?view=connected-software&connect=<code> (jamais de clé dans le
 * lien). Callizy signe chaque requête en silence ; le secret, pour qui veut le
 * vérifier, se crée dans « Modifier », Réglages avancés.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router'
import { ChevronRight, Loader2, Plug, Plus, Settings2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAIAgents } from '@/api/aiAgents'
import {
  useApiActionModele,
  useApiActionModeles,
  useApiActions,
  useDeleteApiAction,
  useSaveApiAction,
  useTestApiActionCall,
  type ApiAction,
  type ApiActionModele,
  type ApiActionTestResult,
} from '@/api/apiActions'
import { useProfile } from '@/api/profile'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { CustomApiDialog, ReponseRecue } from './CustomApiDialog'
import { erreurServeur, Ligne } from './parts'

type Langue = 'fr' | 'en'

/**
 * Sans agentId : la page ouverte par le lien d'un logiciel partenaire (aucun menu
 * n'y mène). Avec agentId : l'onglet « Logiciels connectés » de la fiche de
 * l'agent, où l'interrupteur dit si CET agent consulte le logiciel.
 */
export function ConnectedSoftwareView({ agentId }: { agentId?: number } = {}) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const langue: Langue = locale === 'en' ? 'en' : 'fr'
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { data: connexions = [], isLoading } = useApiActions(companyId)
  const { data: modeles = [] } = useApiActionModeles(companyId)
  const { data: agents = [] } = useAIAgents(companyId)
  const enregistrer = useSaveApiAction(companyId)
  const retirer = useDeleteApiAction(companyId)

  const [assistant, setAssistant] = useState(false)
  const [perso, setPerso] = useState<ApiAction | 'new' | null>(null)
  // Lien direct d'un logiciel partenaire : ouvre le formulaire prérempli.
  const [searchParams, setSearchParams] = useSearchParams()
  const codeLien = searchParams.get('connect')
  const { data: modeleLien, isError: lienInconnu } = useApiActionModele(companyId, codeLien)
  const [prerempli, setPrerempli] = useState<ApiActionModele | null>(null)
  useEffect(() => {
    if (!codeLien || (!modeleLien && !lienInconnu)) return
    if (modeleLien) {
      setPrerempli(modeleLien)
      setPerso('new')
    }
    // Code connu ou non, le paramètre a servi : l'adresse redevient la page normale.
    const next = new URLSearchParams(searchParams)
    next.delete('connect')
    setSearchParams(next, { replace: true })
  }, [codeLien, modeleLien, lienInconnu, searchParams, setSearchParams])
  const [aTester, setATester] = useState<ApiAction | null>(null)
  const [aRetirer, setARetirer] = useState<number | null>(null)

  const nomsAgents = (c: ApiAction) => agents.filter((a) => c.agent_ids.includes(a.id)).map((a) => a.name)

  const basculer = async (c: ApiAction, on: boolean) => {
    const data = agentId
      ? { agent_ids: on ? [...c.agent_ids, agentId] : c.agent_ids.filter((a) => a !== agentId) }
      : { is_active: on }
    try {
      await enregistrer.mutateAsync({ id: c.id, data })
    } catch (e) {
      toast.error(erreurServeur(e) || t('manager.connected.errSave', 'Impossible d’enregistrer'))
    }
  }

  const confirmerRetrait = async (id: number) => {
    try {
      await retirer.mutateAsync(id)
      setARetirer(null)
      toast.success(t('manager.connected.removed', 'Connexion retirée'))
    } catch (e) {
      toast.error(erreurServeur(e) || t('manager.connected.errSave', 'Impossible d’enregistrer'))
    }
  }

  return (
    <div className={agentId ? '' : 'h-full overflow-y-auto'}>
      <div className={agentId ? 'space-y-6' : 'px-6 lg:px-8 py-6 space-y-6'}>
        <div className="space-y-6 max-w-5xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className={agentId ? 'text-lg font-semibold text-slate-900 dark:text-white' : 'text-xl font-semibold text-slate-900 dark:text-white'}>{t('manager.connected.title', 'Logiciels connectés')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {agentId
                ? t('manager.connected.subtitleAgent', 'Cet agent consulte ces logiciels pendant l’appel.')
                : t('manager.connected.subtitle', 'Vos agents consultent vos logiciels pendant l’appel.')}
              <br />
              {t('manager.connected.subtitle2', 'Ils répondent avec vos vraies données.')}
            </p>
          </div>
          <Button type="button" onClick={() => setAssistant(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('manager.connected.connect', 'Connecter un logiciel')}
          </Button>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
          {isLoading ? (
            <div className="py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : connexions.length === 0 ? (
            <div className="py-12 text-center px-6">
              <Plug className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-slate-600 dark:text-slate-300">{t('manager.connected.emptyTitle', 'Aucun logiciel connecté.')}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('manager.connected.emptySubtitle', 'Connectez un logiciel pour que vos agents y trouvent les réponses.')}</p>
            </div>
          ) : (
            connexions.map((c) => {
              const noms = nomsAgents(c)
              return (
                <div key={c.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      <Plug className="h-[18px] w-[18px]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{c.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {noms.length ? noms.join(', ') : t('manager.connected.noAgentUses', 'Aucun agent ne l’utilise')}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      {agentId ? (
                        <>
                          <Switch checked={c.agent_ids.includes(agentId)} disabled={enregistrer.isPending} onCheckedChange={(v) => basculer(c, v)} />
                          {c.agent_ids.includes(agentId) ? t('manager.connected.usedByAgent', 'Utilisé') : t('manager.connected.notUsedByAgent', 'Non utilisé')}
                        </>
                      ) : (
                        <>
                          <Switch checked={c.is_active} disabled={enregistrer.isPending} onCheckedChange={(v) => basculer(c, v)} />
                          {c.is_active ? t('manager.connected.active', 'Active') : t('manager.connected.paused', 'En pause')}
                        </>
                      )}
                    </label>
                    <div className="flex items-center gap-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setATester(c)}>{t('manager.connected.test', 'Tester')}</Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setPerso(c)}>{t('manager.connected.edit', 'Modifier')}</Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setARetirer(c.id)}>{t('manager.connected.remove', 'Retirer')}</Button>
                    </div>
                  </div>
                  {aRetirer === c.id && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3">
                      <p className="text-sm text-slate-700 dark:text-slate-200">
                        {t('manager.connected.removeConfirm', 'Retirer cette connexion ?')}
                        <br />
                        <span className="text-xs text-slate-500 dark:text-slate-400">{t('manager.connected.removeHelp', 'Vos agents ne pourront plus consulter ce logiciel.')}</span>
                      </p>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setARetirer(null)}>{t('manager.connected.cancel', 'Annuler')}</Button>
                        <Button type="button" variant="destructive" size="sm" disabled={retirer.isPending} onClick={() => confirmerRetrait(c.id)}>{t('manager.connected.remove', 'Retirer')}</Button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
        </div>
      </div>

      {assistant && (
        <ChoixLogiciel modeles={modeles} langue={langue}
          onClose={() => setAssistant(false)}
          onChoix={(m) => { setAssistant(false); setPrerempli(m); setPerso('new') }} />
      )}
      {perso && (
        <CustomApiDialog companyId={companyId} agents={agents} action={perso === 'new' ? null : perso}
          prerempli={perso === 'new' ? prerempli : null} agentDefaut={agentId}
          onClose={() => { setPerso(null); setPrerempli(null) }} />
      )}
      {aTester && (
        <Dialog open onOpenChange={(o) => !o && setATester(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('manager.connected.testTitle', 'Tester avec un numéro de téléphone')}</DialogTitle>
            </DialogHeader>
            <TestParTelephone companyId={companyId} connexion={aTester} langue={langue} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

/** « Connecter un logiciel » : un modèle tout prêt du catalogue (qui
 *  préremplit le formulaire), ou « Autre logiciel » (formulaire vide). */
function ChoixLogiciel({ modeles, langue, onClose, onChoix }: {
  modeles: ApiActionModele[]; langue: Langue
  onClose: () => void
  /** null = « Autre logiciel » */
  onChoix: (m: ApiActionModele | null) => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('manager.connected.connect', 'Connecter un logiciel')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {modeles.map((m) => (
            <button key={m.id} type="button" onClick={() => onChoix(m)}
              className="w-full flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"><Plug className="h-[18px] w-[18px]" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-900 dark:text-white">{m.logiciel} · {m.sous_titre[langue]}</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">{m.phrase[langue]}</span>
              </span>
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </button>
          ))}
          <button type="button" onClick={() => onChoix(null)}
            className="w-full flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"><Settings2 className="h-[18px] w-[18px]" /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-900 dark:text-white">{t('manager.connected.other', 'Autre logiciel')}</span>
              <span className="block text-xs text-slate-500 dark:text-slate-400">{t('manager.connected.otherHelp', 'Pour un logiciel qui a une API : une adresse et une clé.')}</span>
            </span>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
          <p className="pt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('manager.connected.notListed', 'Votre logiciel n’est pas dans la liste ?')}{' '}
            <a href="mailto:support@callizy.ca" className="font-medium text-slate-700 dark:text-slate-200 underline">
              {t('manager.connected.writeUs', 'Écrivez-nous, on le branche pour vous.')}
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Un numéro de téléphone de test à la place de celui de l'appelant : ce que recevra l'agent. */
function TestParTelephone({ companyId, connexion, langue }: { companyId: number | undefined; connexion: ApiAction; langue: Langue }) {
  const { t } = useTranslation()
  const [tel, setTel] = useState('')
  const [resultat, setResultat] = useState<ApiActionTestResult | null>(null)
  const essai = useTestApiActionCall(companyId)

  const tester = async () => {
    try {
      setResultat(await essai.mutateAsync({ id: connexion.id, telephone: tel.trim(), language: langue }))
    } catch (e) {
      toast.error(erreurServeur(e) || t('manager.apiAction.errTest', 'Essai impossible'))
    }
  }

  return (
    <div className="space-y-3">
      <Ligne label={t('manager.connected.testNumber', 'Numéro de téléphone d’un client')} aide={t('manager.connected.testHelp', 'Comme si ce client appelait votre agent.')}>
        <div className="flex gap-2">
          <Input value={tel} onChange={(e) => setTel(e.target.value)} placeholder="+15145550123" />
          <Button type="button" variant="outline" disabled={!tel.trim() || essai.isPending} onClick={tester}>
            {essai.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('manager.connected.test', 'Tester')}
          </Button>
        </div>
      </Ligne>
      {resultat && <ReponseRecue resultat={resultat} />}
    </div>
  )
}
